import { useState, useMemo } from "react";
import { Map, GoogleApiWrapper, Polyline, Marker } from "google-maps-react";
import { Button, Spinner, Modal } from "react-bootstrap";
import locStart from "../assets/Icons/Start.png";
import locEnd from "../assets/Icons/End.png";
import latest from "../assets/Icons/Latest.png";

function PermissionHelpModal({ show, platform, onClose }) {
  const steps = useMemo(() => {
    if (platform === "ios") {
      return [
        "Open Settings → Privacy & Security → Location Services.",
        "Ensure Location Services is ON.",
        "Scroll to Safari Websites (or your browser) and set it to “While Using the App” or “Ask Next Time or When I Share.",
        "Return to the page, reload it, and then tap Get Directions again.",
      ];
    }
    if (platform === "android") {
      return [
        "Open Settings → Apps → your browser (Chrome, etc.).",
        "Tap Permissions → Location.",
        "Set to “Allow only while using the app” or “Ask every time”.",
        "Return to the site and tap ‘Get Directions’ again.",
      ];
    }
    return [
      "Open your browser’s Site settings for this page.",
      "Find Location and allow it (or clear blocked permissions).",
      "Reload the page and try again.",
    ];
  }, [platform]);

  const title =
    platform === "ios"
      ? "Enable Location on iOS"
      : platform === "android"
      ? "Enable Location on Android"
      : "Enable Location";

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"      
      keyboard={false}       // << escape won't close
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ol className="mb-0">
          {steps.map((s, i) => (
            <li key={i} className="mb-2">
              {s}
            </li>
          ))}
        </ol>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onClose}>
          Got it
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function MapBle(param) {
  const [loading, setLoading] = useState(false); 
  const [showPermHelp, setShowPermHelp] = useState(false);
  const [helpPlatform, setHelpPlatform] = useState("generic"); // ios | android | generic

  // --- data prep ---
  const mapData = param?.passId?.alarmLocationDetails;
  const endDateLog = param?.passId?.userAlertData?.endedAt;

  const locations = mapData
  ? endDateLog !== "" ? mapData
          .filter(
            (coords) =>
              coords.latitude !== "" &&
              !coords.longitude.includes("e") &&
              new Date(endDateLog) >= new Date(coords?.logTime)
          )
          .map((coords, index) => ({
            id: index,
            lat: parseFloat(coords.latitude),
            lng: parseFloat(coords.longitude),
            time: coords.logTime,
      })) : 
      mapData
          .filter(
            (coords) =>
          coords.latitude !== "" &&
          !coords.longitude.includes("e")
          )
          .map((coords, index) => ({
            id: index,
            lat: parseFloat(coords.latitude),
            lng: parseFloat(coords.longitude),
            time: coords.logTime,
          }))
    : [];

  const loclen = locations.length - 1;

  const hoverDate = (e, coords) => {
    const latitude = coords.position.lat();
    const longitude = coords.position.lng();
    alert(
      `Latitude: ${latitude}\nLongitude: ${longitude}\nTime: ${coords.title}`
    );
  };

  // --- platform helpers ---
  const ua =
    typeof navigator !== "undefined" ? navigator.userAgent.toLowerCase() : "";
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isAndroid = /android/.test(ua);
  const isMobile = isIOS || isAndroid;

  // --- iOS: single Apple Maps open; reload if user cancels the “Open in Maps?” sheet ---
const openAppleMaps = (url) => {
  const cleanup = () => {
    document.removeEventListener("visibilitychange", onVis);
    window.removeEventListener("pagehide", onHide, { capture: true });
    window.removeEventListener("blur", onHide);
  };

  const onHide = () => {
    cleanup();
    setLoading(false);
  };
  const onVis = () => {
    if (document.visibilityState === "hidden") onHide();
  };

  document.addEventListener("visibilitychange", onVis, { once: true });
  window.addEventListener("pagehide", onHide, { capture: true, once: true });
  window.addEventListener("blur", onHide, { once: true });

  try {
    window.location.assign(url);
  } catch (e) {
    
    setLoading(false);
    cleanup(); 
  }
  setTimeout(() => {
    setLoading(false);
    cleanup();
  }, 2500); 
};

  // --- generic fallbacks (Android keeps this; Desktop not used) ---
  const openWithFallbacks = (urls, delays) => {
    const timers = [];

    const cleanup = () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onHide, { capture: true });
      window.removeEventListener("blur", onHide);
    };

    const cancelFallbacks = () => {
      while (timers.length) clearTimeout(timers.pop());
      cleanup();
      setLoading(false);
    };

    const onHide = () => cancelFallbacks();
    const onVis = () => {
      if (document.visibilityState === "hidden") onHide();
    };

    document.addEventListener("visibilitychange", onVis, { once: true });
    window.addEventListener("pagehide", onHide, { capture: true, once: true });
    window.addEventListener("blur", onHide, { once: true });

    try {
      window.location.href = urls[0];
    } catch {}

    for (let i = 1; i < urls.length; i++) {
      const delay =
        Array.isArray(delays) && typeof delays[i] === "number" ? delays[i] : 1200;
      timers.push(
        setTimeout(() => {
          try {
            window.location.href = urls[i];
          } catch {}
          if (i === urls.length - 1) {
            cleanup();
            setLoading(false);
          }
        }, delay)
      );
    }
  };

  // --- URL builders ---
  const buildUrls = (endLat, endLng, origin) => {
    if (isIOS) {
      const daddr = `${endLat},${endLng}`;
      if (origin) {
        const { lat: oLat, lng: oLng } = origin;
        const saddr = `${oLat},${oLng}`;
        return [`https://maps.apple.com/?saddr=${encodeURIComponent(saddr)}&daddr=${encodeURIComponent(daddr)}&dirflg=d`];
      }
      return [`https://maps.apple.com/?daddr=${encodeURIComponent(daddr)}&dirflg=d`];
    } else if (isAndroid) {
      if (origin) {
        const { lat: oLat, lng: oLng } = origin;
        return [
          `google.navigation:q=${endLat},${endLng}&mode=d`,
          `geo:${endLat},${endLng}?q=${encodeURIComponent(`${endLat},${endLng}`)}`,
          `https://www.google.com/maps/dir/?api=1&origin=${oLat},${oLng}&destination=${endLat},${endLng}&travelmode=driving`,
        ];
      }
      return [
        `google.navigation:q=${endLat},${endLng}&mode=d`,
        `geo:${endLat},${endLng}?q=${encodeURIComponent(`${endLat},${endLng}`)}`,
        `https://www.google.com/maps/dir/?api=1&destination=${endLat},${endLng}&travelmode=driving`,
      ];
    } else {
      if (origin) {
        const { lat: oLat, lng: oLng } = origin;
        return [
          `https://www.google.com/maps/dir/?api=1&origin=${oLat},${oLng}&destination=${endLat},${endLng}`,
        ];
      }
      return [`https://www.google.com/maps/dir/?api=1&destination=${endLat},${endLng}`];
    }
  };

  const openWithOriginOrNot = (endLat, endLng, origin) => {
    const urls = buildUrls(endLat, endLng, origin);

    if (isIOS) {
      openAppleMaps(urls[0]);
      return;
    }

    if (isAndroid) {
      openWithFallbacks(urls, [0, 1200, 2200]);
      return;
    }

    window.open(urls[0], "_blank", "noopener,noreferrer");
    setLoading(false);
  };

  const showMobilePermissionHelp = () => {
    setHelpPlatform(isIOS ? "ios" : isAndroid ? "android" : "generic");
    setShowPermHelp(true);
  };

  // --- MAIN: open directions flow ---
  const openDirections = async () => {
    if (!locations.length) {
      alert("No end location found.");
      return;
    }

    setLoading(true);
    const endLat = locations[loclen].lat;
    const endLng = locations[loclen].lng;

    // Desktop path
    if (!isMobile) {
      try {
        if (navigator.permissions?.query) {
          const status = await navigator.permissions.query({ name: "geolocation" });
          if (status.state === "denied") {
           setLoading(false);
    showMobilePermissionHelp(); // <-- show the modal on desktop too
     return;
          }
        }
      } catch {}

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          openWithOriginOrNot(endLat, endLng, origin);
        },
        (err) => {
          if (err?.code === 1) {
           setLoading(false);
   showMobilePermissionHelp(); 
  return;
          }
          openWithOriginOrNot(endLat, endLng, null);
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
      );
      return;
    }

    // Mobile (iOS/Android)
    let permissionState = "unknown";
    try {
      if (navigator.permissions?.query) {
        const st = await navigator.permissions.query({ name: "geolocation" });
        permissionState = st.state; // granted | denied | prompt
      }
    } catch {}

    if (permissionState === "denied") {
      showMobilePermissionHelp();
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        openWithOriginOrNot(endLat, endLng, {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      (err) => {
        if (err?.code === 1) {
          showMobilePermissionHelp();
          setLoading(false);
          return;
        }
        openWithOriginOrNot(endLat, endLng, null);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
    );
  };

  return (
    <>
      <PermissionHelpModal
        show={showPermHelp}
        platform={helpPlatform}
        onClose={() => setShowPermHelp(false)}
      />

      {locations.length !== 0 ? (
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "600px",
            backgroundColor: "#f0f0f0",
          }}
        >
         {/*  Button with Spinner */}
          <Button
            onClick={openDirections}
            disabled={loading || showPermHelp}
            variant={loading ? "secondary" : "primary"}
            className="direction-btn"
            style={
              showPermHelp
                ? { opacity: 0, pointerEvents: "none" }
                : undefined
            }
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Loading...
              </>
            ) : (
              "Get Directions"
            )}
          </Button>

          <Map
            google={param.google}
            containerStyle={{
              position: "relative",
              width: "100%",
              height: "100%",
              minHeight: "600px", // ensures visible even if parent collapses
            }}
            style={{
              width: "100%",
              height: "100%",
            }}
            center={locations[locations.length - 1]}
            initialCenter={locations[locations.length - 1]}
            zoom={locations.length === 1 ? 18 : 14}
            disableDefaultUI={true}
            mapTypeControl={true}
            mapTypeControlOptions={{
              style: param.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
              position: param.google.maps.ControlPosition.TOP_RIGHT,
            }}
          >
            <Polyline
              path={locations}
              options={{
                strokeColor: "#0000FF",
                strokeOpacity: 1,
                strokeWeight: 2,
              }}
            />

            <Marker
              name={"Start position"}
              position={{ lat: locations[0].lat, lng: locations[0].lng }}
            icon={{
              url: locStart,
            }}
            title={new Date(locations[0].time).toLocaleString([], { hour12: true })}
            onClick={() => hoverDate(new Date(locations[0].time).toLocaleString([], { hour12: true }))}
            />

            {param?.isEventStop ? (
              <Marker
                name={"End position"}
              position={{ lat: locations[loclen].lat, lng: locations[loclen].lng }}
              icon={{
                url: locEnd,
              }}
              title={new Date(locations[loclen].time).toLocaleString([], { hour12: true })}
              onClick={() => hoverDate(new Date(locations[loclen].time).toLocaleString([], { hour12: true }))}
              />
            ) : (
              <Marker
                name={"End position"}
              position={{ lat: locations[loclen].lat, lng: locations[loclen].lng }}
              icon={{
                url: latest,
              }}
              title={new Date(locations[loclen].time).toLocaleString([], { hour12: true })}
              onClick={() => hoverDate(new Date(locations[loclen].time).toLocaleString([], { hour12: true }))}
              />
            )}
          </Map>
        </div>
      ) : (
        "No Data Found"
      )}
    </>
  );
}

export default GoogleApiWrapper({
  apiKey: "AIzaSyBW0xHIrT1e7VtoO7GsldiWk3p98QxSmm4",
})(MapBle);
